import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, ApiError } from '../shared/api';
import { formatNumber, formatDate } from '../shared/formatting';

type Locale = 'hr' | 'en';
type TradeClass = 'hedge' | 'trading';

type ClassSum = { bought_oz: number; sold_oz: number; open_oz: number; open_fine_g: number; trades: number };
type Trade = {
    source: 'stonex' | 'deal'; id: number; date: string; side: 'BUY' | 'SELL'; pair: string;
    quantity_oz: number; fine_g: number; price: number | null; remarks: string;
    class: TradeClass; auto_class: TradeClass; reason: string; overridden: boolean;
};
type Resp = { summary: { has_data: boolean; env: string; cutover: string | null; hedge: ClassSum; trading: ClassSum }; env: string; cutover: string | null; trades: Trade[] };

/**
 * N7 (TASK-40-61): nalozi iz modula Hedging koji cine H1 (hedge) i H2
 * (trading), s razlogom klase i rucnom promjenom. Isti panel koriste 40.61
 * (detalj retka H1) i 40.62. V3 (Vedran 12.10.2026): klase su H5 hedge (puni H1 Hrvatska) i H6 trading.
 */
export function HedgeTradesPanel({ locale, token, canEdit, onChanged }: { locale: Locale; token: string | null; canEdit: boolean; onChanged?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Resp | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api<Resp>('/gold-coverage-v2/hedge-trades', token)
            .then(setData)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje naloga nije uspjelo.', 'Loading trades failed.')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);
    useEffect(load, [load]);

    const setClass = async (tr: Trade, cls: TradeClass | null) => {
        setBusy(true);
        setError(null);
        try {
            await mutate('/gold-coverage-v2/hedge-trades/class', token, 'PUT', { source: tr.source, id: tr.id, class: cls });
            load();
            onChanged?.();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Promjena nije uspjela.', 'Update failed.'));
        } finally {
            setBusy(false);
        }
    };

    if (!data) return error ? <p className="error">{error}</p> : <p className="muted">{t('Učitavam naloge…', 'Loading trades…')}</p>;
    const s = data.summary;

    return (
        <div>
            {!s.has_data ? (
                <p className="muted">{t(`Hedging nema naloga u okruženju ${data.env}; H1 (H5) i H6 dolaze iz ručnog unosa.`, `Hedging has no trades in environment ${data.env}; H1 (H5) and H6 come from manual entry.`)}</p>
            ) : (
                <table className="table" style={{ marginBottom: '0.75rem' }}>
                    <thead><tr><th></th><th>{t('Kupljeno oz', 'Bought oz')}</th><th>{t('Prodano oz', 'Sold oz')}</th><th>{t('Otvoreno oz', 'Open oz')}</th><th>{t('Otvoreno g', 'Open g')}</th><th>{t('Naloga', 'Trades')}</th></tr></thead>
                    <tbody>
                        {([['hedge', 'H5 hedge'], ['trading', 'H6 trading']] as [TradeClass, string][]).map(([k, label]) => (
                            <tr key={k}>
                                <td>{label}</td><td>{formatNumber(s[k].bought_oz, locale)}</td><td>{formatNumber(s[k].sold_oz, locale)}</td>
                                <td><strong>{formatNumber(s[k].open_oz, locale)}</strong></td><td>{formatNumber(s[k].open_fine_g, locale)}</td><td>{s[k].trades}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {data.cutover ? <p className="muted" style={{ fontSize: 12 }}>{t(`Izvod StoneX vrijedi do ${formatDate(data.cutover, locale)}, od tog dana PMX nalozi (${data.env}).`, `StoneX statement applies until ${formatDate(data.cutover, locale)}, PMX orders from that day (${data.env}).`)}</p> : null}
            {error ? <p className="error">{error}</p> : null}
            {data.trades.length > 0 ? (
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    <table className="table">
                        <thead><tr><th>{t('Datum', 'Date')}</th><th>{t('Izvor', 'Source')}</th><th></th><th>{t('Par', 'Pair')}</th><th>oz</th><th>{t('Klasa', 'Class')}</th><th>{t('Razlog', 'Reason')}</th><th>{t('Napomena', 'Remarks')}</th></tr></thead>
                        <tbody>
                            {data.trades.map((tr) => (
                                <tr key={`${tr.source}-${tr.id}`}>
                                    <td>{formatDate(tr.date, locale)}</td>
                                    <td>{tr.source === 'stonex' ? 'PMX' : t('izvod', 'statement')}</td>
                                    <td>{tr.side}</td>
                                    <td>{tr.pair}</td>
                                    <td>{formatNumber(tr.quantity_oz, locale)}</td>
                                    <td>
                                        {canEdit ? (
                                            <select value={tr.class} disabled={busy} onChange={(e) => void setClass(tr, e.target.value === tr.auto_class ? null : (e.target.value as TradeClass))}>
                                                <option value="hedge">hedge (H1)</option>
                                                <option value="trading">trading (H2)</option>
                                            </select>
                                        ) : tr.class}
                                    </td>
                                    <td>{tr.reason}</td>
                                    <td>{tr.remarks}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}
